import { routeUrl } from "@/app/routes";
import {
  READ_CARD_ROW_LIMIT,
  ReadCardFrame,
} from "@/features/copilot/components/read-cards/read-card-frame";
import type { GoalItem } from "@/features/copilot/schemas/copilot-read-cards.schema";
import { Progress } from "@/shared/components/ui/progress";
import { formatDateOnly } from "@/shared/lib/format-date-only";

/// An unknown health shows nothing rather than a raw enum.
const GOAL_HEALTH_LABEL: Record<string, string> = {
  ahead: "Ahead",
  on_track: "On track",
  at_risk: "At risk",
  behind: "Behind",
};

function goalMeta(goal: GoalItem): string {
  const due = formatDateOnly(goal.dueDate ?? null);
  return [
    goal.progress === undefined ? null : `${Math.round(goal.progress)}%`,
    goal.health ? (GOAL_HEALTH_LABEL[goal.health] ?? null) : null,
    due ? `Due ${due}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

export function GoalsReadCard({
  items,
  slug,
  hasMore = false,
}: {
  items: GoalItem[];
  slug: string;
  hasMore?: boolean;
}) {
  const shown = items.slice(0, READ_CARD_ROW_LIMIT);
  return (
    <ReadCardFrame
      title="Goals"
      viewAllHref={routeUrl.goals.list(slug)}
      hiddenCount={items.length - shown.length}
      hasMore={hasMore}
    >
      {shown.map((goal) => (
        <li key={goal.id}>
          <a
            href={routeUrl.goals.detail(slug, goal.id)}
            className="block space-y-0.5 px-3 py-2 hover:bg-muted/60 focus-visible:outline-2 focus-visible:outline-ring"
          >
            <span className="line-clamp-2 break-words">{goal.untrustedTitle}</span>
            {goal.progress === undefined ? null : (
              <Progress
                aria-hidden="true"
                className="h-1"
                value={Math.min(100, Math.max(0, goal.progress))}
              />
            )}
            <span className="block truncate text-xs text-text-secondary">{goalMeta(goal)}</span>
          </a>
        </li>
      ))}
    </ReadCardFrame>
  );
}
